import React, { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import MaterialTable, { Column } from "material-table";
import BreadcrumbNav from "../../../../components/BreadcrumbNav/BreadcrumbNav";
import CustomButton from "../../../../components/Button/CustomButton";
import DeleteConfirmationDialog from "../../../../components/DeleteConfirmationDialog/DeleteConfirmationDialog";
import { materialTableES } from "../../../../services/materialTableES";
import { errorToast, successToast } from "../../../../services/toasts";
import {
  useCreateRbacRole,
  useDeleteRbacRole,
  useGetRbacPermissions,
  useGetRbacRoles,
  useSetRbacRolePermissions,
  useUpdateRbacRole,
} from "../../../../utils/react-query/queryRbac/queries";
import type {
  SupplierRbacPermission,
  SupplierRbacRole,
  SupplierRoleFormValues,
} from "../../types";
import { groupPermissionsByModule, moduleIconFor } from "./permissionGroups";
import type { PermissionSubmenu } from "./permissionGroups";
import styles from "./RolesManagement.module.scss";

const INITIAL_ROLE_FORM: SupplierRoleFormValues = {
  name: "",
  description: "",
  permission_ids: [],
};

/** S1: counter shown in the header of every module and submenu. */
const permissionCountLabel = (count: number) =>
  `${count} ${count === 1 ? "permiso" : "permisos"}`;

const getErrorMessage = (error: unknown, fallbackMessage: string) => {
  return error instanceof Error ? error.message : fallbackMessage;
};

const RolesManagement = () => {
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<SupplierRbacRole | null>(
    null
  );
  const [roleForm, setRoleForm] =
    useState<SupplierRoleFormValues>(INITIAL_ROLE_FORM);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    onConfirm: () => {},
  });

  const { data: roles = [], isLoading: isLoadingRoles } = useGetRbacRoles();
  const {
    data: permissions = [],
    isLoading: isLoadingPermissions,
    isError: hasPermissionsError,
    error: permissionsError,
  } = useGetRbacPermissions();
  const { mutateAsync: createRole, isLoading: isCreatingRole } =
    useCreateRbacRole();
  const { mutateAsync: updateRole, isLoading: isUpdatingRole } =
    useUpdateRbacRole();
  const { mutateAsync: deleteRole } = useDeleteRbacRole();
  const { mutateAsync: setRolePermissions, isLoading: isSavingPermissions } =
    useSetRbacRolePermissions();

  const summary = useMemo(() => {
    const systemRoles = roles.filter((role) => role.is_system).length;

    return {
      totalRoles: roles.length,
      systemRoles,
      customRoles: roles.length - systemRoles,
    };
  }, [roles]);

  // S1: every module card lists its permissions grouped in submenus
  // (Ver, Crear, Editar, Eliminar first, then the rest in API order).
  const moduleGroups = useMemo(
    () => groupPermissionsByModule(permissions),
    [permissions]
  );

  const permissionsUnavailable =
    !isLoadingPermissions && !hasPermissionsError && permissions.length === 0;
  const canSubmitRole =
    !isLoadingPermissions &&
    !hasPermissionsError &&
    !permissionsUnavailable &&
    !isCreatingRole &&
    !isUpdatingRole &&
    !isSavingPermissions;

  const openCreateDialog = () => {
    setSelectedRole(null);
    setRoleForm(INITIAL_ROLE_FORM);
    setIsRoleDialogOpen(true);
  };

  const openEditDialog = (role: SupplierRbacRole) => {
    setSelectedRole(role);
    setRoleForm({
      name: role.name,
      description: role.description || "",
      permission_ids: role.permissions.map((permission) => permission.id),
    });
    setIsRoleDialogOpen(true);
  };

  const closeRoleDialog = () => {
    setSelectedRole(null);
    setRoleForm(INITIAL_ROLE_FORM);
    setIsRoleDialogOpen(false);
  };

  const togglePermission = (permissionId: number) => {
    setRoleForm((prev) => ({
      ...prev,
      permission_ids: prev.permission_ids.includes(permissionId)
        ? prev.permission_ids.filter(
            (currentPermissionId) => currentPermissionId !== permissionId
          )
        : [...prev.permission_ids, permissionId],
    }));
  };

  const handleSaveRole = async () => {
    if (isLoadingPermissions || hasPermissionsError || permissionsUnavailable) {
      return errorToast(
        hasPermissionsError
          ? getErrorMessage(
              permissionsError,
              "No fue posible cargar los permisos del backend"
            )
          : permissionsUnavailable
          ? "No hay permisos disponibles para asignar a este rol"
          : "Espera a que se carguen los permisos antes de guardar el rol"
      );
    }

    if (!selectedRole && !roleForm.name.trim()) {
      return errorToast("El nombre del rol es obligatorio");
    }

    try {
      let roleId = selectedRole?.id;

      if (selectedRole) {
        await updateRole({
          roleId: selectedRole.id,
          data: {
            name: roleForm.name.trim(),
            description: roleForm.description.trim(),
          },
        });
      } else {
        const createdRole = await createRole({
          name: roleForm.name.trim(),
          description: roleForm.description.trim(),
        });
        roleId = createdRole.id;
      }

      if (roleId) {
        await setRolePermissions({
          roleId,
          permission_ids: roleForm.permission_ids,
        });
      }

      successToast(
        selectedRole ? "Rol actualizado con éxito" : "Rol creado con éxito"
      );
      closeRoleDialog();
    } catch (error) {
      errorToast(getErrorMessage(error, "No fue posible guardar el rol"));
    }
  };

  const handleDeleteRole = async (roleId: number) => {
    try {
      await deleteRole(roleId);
      successToast("Rol eliminado con éxito");
    } catch (error) {
      errorToast(getErrorMessage(error, "No fue posible eliminar el rol"));
    }
  };

  const renderPermissionCheckbox = (permission: SupplierRbacPermission) => (
    <Checkbox
      color="primary"
      checked={roleForm.permission_ids.includes(permission.id)}
      onChange={() => togglePermission(permission.id)}
    />
  );

  // S7: a family with a single permission is a standalone row labelled with the
  // family itself, with no submenu header.
  const renderSinglePermissionOption = (submenu: PermissionSubmenu) => {
    const [permission] = submenu.permissions;

    return (
      <FormControlLabel
        key={submenu.key}
        className={styles.singlePermissionOption}
        control={renderPermissionCheckbox(permission)}
        label={
          <Typography component="span" className={styles.primaryText}>
            {submenu.label}
          </Typography>
        }
      />
    );
  };

  // S1: submenus of two or more options are expanded when the module opens, so
  // one click is enough to see them. S3: every option shows only its
  // description, never its key.
  const renderPermissionSubmenu = (
    submenu: PermissionSubmenu,
    moduleIndex: number,
    submenuIndex: number
  ) => (
    <Accordion
      key={submenu.key}
      className={styles.submenuAccordion}
      elevation={0}
      defaultExpanded
      TransitionProps={{ unmountOnExit: true }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon className={styles.submenuExpandIcon} />}
        aria-controls={`role-submenu-${moduleIndex}-${submenuIndex}-permissions`}
        id={`role-submenu-${moduleIndex}-${submenuIndex}-header`}
      >
        <div className={styles.submenuHeader}>
          <Typography
            component="h4"
            variant="subtitle2"
            className={styles.submenuTitle}
          >
            {submenu.label}
          </Typography>
          <span className={styles.submenuCount}>
            {permissionCountLabel(submenu.permissions.length)}
          </span>
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <div
          className={styles.permissionsList}
          role="group"
          aria-label={submenu.label}
        >
          {submenu.permissions.map((permission) => (
            <FormControlLabel
              key={permission.id}
              className={styles.permissionOption}
              control={renderPermissionCheckbox(permission)}
              label={
                <Typography component="span" className={styles.primaryText}>
                  {permission.description}
                </Typography>
              }
            />
          ))}
        </div>
      </AccordionDetails>
    </Accordion>
  );

  const renderPermissionsSelector = () => {
    if (isLoadingPermissions) {
      return (
        <Paper className={styles.emptyState} elevation={0}>
          <Typography className={styles.primaryText}>
            Cargando permisos desde el backend...
          </Typography>
          <Typography className={styles.secondaryText}>
            Espera a que termine la consulta para configurar el rol.
          </Typography>
        </Paper>
      );
    }

    if (hasPermissionsError) {
      return (
        <Paper className={styles.emptyState} elevation={0}>
          <Typography className={styles.primaryText}>
            No fue posible cargar los permisos
          </Typography>
          <Typography className={styles.secondaryText}>
            {getErrorMessage(
              permissionsError,
              "Verifica la respuesta de /v2/rbac/permissions e inténtalo nuevamente."
            )}
          </Typography>
        </Paper>
      );
    }

    if (permissionsUnavailable) {
      return (
        <Paper className={styles.emptyState} elevation={0}>
          <Typography className={styles.primaryText}>
            Aún no hay permisos disponibles
          </Typography>
          <Typography className={styles.secondaryText}>
            El backend no devolvió permisos para este proveedor.
          </Typography>
        </Paper>
      );
    }

    return (
      <Grid container spacing={2}>
        {moduleGroups.map(({ module, submenus }, moduleIndex) => {
          const ModuleIcon = moduleIconFor(module);
          const modulePermissionCount = submenus.reduce(
            (total, submenu) => total + submenu.permissions.length,
            0
          );

          return (
            <Grid item xs={12} md={6} key={module}>
              {/* S1: modules start collapsed and expand with one click. */}
              <Accordion
                className={styles.moduleAccordion}
                elevation={0}
                TransitionProps={{ unmountOnExit: true }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon className={styles.moduleExpandIcon} />
                  }
                  aria-controls={`role-module-${moduleIndex}-permissions`}
                  id={`role-module-${moduleIndex}-header`}
                >
                  <div className={styles.moduleHeader}>
                    <ModuleIcon
                      className={styles.moduleIcon}
                      data-testid="module-icon"
                    />
                    <Typography
                      component="h3"
                      variant="subtitle1"
                      className={styles.moduleTitle}
                    >
                      {module}
                    </Typography>
                    <span className={styles.moduleCount}>
                      {permissionCountLabel(modulePermissionCount)}
                    </span>
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <div className={styles.moduleDetailsContent}>
                    {submenus.map((submenu, submenuIndex) =>
                      submenu.permissions.length === 1
                        ? renderSinglePermissionOption(submenu)
                        : renderPermissionSubmenu(
                            submenu,
                            moduleIndex,
                            submenuIndex
                          )
                    )}
                  </div>
                </AccordionDetails>
              </Accordion>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const columns: Column<SupplierRbacRole>[] = [
    {
      title: "Rol",
      field: "name",
      render: (rowData) => (
        <div>
          <Typography className={styles.primaryText}>{rowData.name}</Typography>
          <Typography className={styles.secondaryText}>
            {rowData.description || "Sin descripción"}
          </Typography>
        </div>
      ),
    },
    {
      title: "Tipo",
      field: "is_system",
      filtering: false,
      render: (rowData) => (
        <span
          className={`${styles.roleTypeChip} ${
            rowData.is_system ? styles.systemRoleTypeChip : ""
          }`}
        >
          <span
            className={`${styles.roleTypeText} ${
              rowData.is_system ? styles.systemRoleTypeText : ""
            }`}
          >
            {rowData.is_system ? "Sistema" : "Personalizado"}
          </span>
        </span>
      ),
    },
    {
      title: "Permisos",
      field: "permissions",
      filtering: false,
      render: (rowData) => `${rowData.permissions?.length || 0} permisos`,
    },
    {
      title: "Acciones",
      field: "actions",
      sorting: false,
      filtering: false,
      render: (rowData) => (
        <div className={styles.rowActions}>
          <Tooltip title="Editar rol">
            <IconButton onClick={() => openEditDialog(rowData)}>
              <EditIcon className={styles.actionIconAccent} />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              rowData.is_system
                ? "Los roles del sistema no se pueden eliminar"
                : "Eliminar rol"
            }
          >
            <span>
              <IconButton
                disabled={rowData.is_system}
                onClick={() =>
                  setConfirmDialog({
                    isOpen: true,
                    title: "Eliminar rol",
                    subTitle:
                      "Solo se eliminará el rol personalizado. ¿Deseas continuar?",
                    onConfirm: async () => {
                      await handleDeleteRole(rowData.id);
                      setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
                    },
                  })
                }
              >
                <DeleteIcon
                  className={
                    rowData.is_system
                      ? styles.actionIconDisabled
                      : styles.actionIconDanger
                  }
                />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <section className={`layout-dashboard-container ${styles.page}`}>
      <div className="header-container">
        <Box className={styles.headerRow}>
          <div>
            <h4 className="header-title">Roles y permisos</h4>
            <BreadcrumbNav
              firstText="Proveedor"
              secondText="Roles y permisos"
              secondLink="/proveedor/roles-permisos"
              thirdText="Gestión"
            />
          </div>
        </Box>
      </div>

      <DeleteConfirmationDialog
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
      />

      <Grid container spacing={2} className={styles.summaryGrid}>
        {[
          { label: "Total roles", value: summary.totalRoles },
          { label: "Roles del sistema", value: summary.systemRoles },
          { label: "Roles personalizados", value: summary.customRoles },
        ].map((item) => (
          <Grid item xs={12} sm={4} key={item.label}>
            <Paper className={styles.summaryCard} elevation={0}>
              <Typography className={styles.summaryLabel}>
                {item.label}
              </Typography>
              <Typography className={styles.summaryValue}>
                {item.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper className={styles.helperCard} elevation={0}>
        <Typography className={styles.primaryText}>
          Define aquí los permisos internos del proveedor.
        </Typography>
        <Typography className={styles.secondaryText}>
          Los roles del sistema se pueden ajustar en permisos y descripción,
          pero no se eliminan.
        </Typography>
      </Paper>

      <div className={styles.actionBar}>
        <CustomButton
          color="secondary"
          shape="little-rounded"
          icon={AddCircleOutlineIcon}
          onClick={openCreateDialog}
        >
          Crear rol
        </CustomButton>
      </div>

      <MaterialTable
        title=""
        columns={columns}
        data={roles}
        isLoading={isLoadingRoles || isLoadingPermissions}
        localization={materialTableES}
        options={{
          search: true,
          filtering: false,
          searchFieldVariant: "outlined",
        }}
      />

      <Dialog
        open={isRoleDialogOpen}
        onClose={closeRoleDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{ className: styles.dialogPaper }}
      >
        <DialogTitle>
          {selectedRole ? `Editar rol: ${selectedRole.name}` : "Crear rol"}
        </DialogTitle>
        <DialogContent dividers className={styles.dialogContent}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombre del rol"
                variant="outlined"
                value={roleForm.name}
                disabled={selectedRole?.is_system}
                onChange={(event) =>
                  setRoleForm((prev) => ({ ...prev, name: event.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Descripción"
                variant="outlined"
                value={roleForm.description}
                onChange={(event) =>
                  setRoleForm((prev) => ({
                    ...prev,
                    description: event.target.value,
                  }))
                }
              />
            </Grid>
          </Grid>

          <Box className={styles.permissionSection}>
            <Typography className={styles.primaryText}>Permisos</Typography>
            <Typography className={styles.secondaryText}>
              Selecciona los permisos que heredará este rol.
            </Typography>
          </Box>

          {renderPermissionsSelector()}
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <CustomButton
            color="outlined"
            shape="little-rounded"
            onClick={closeRoleDialog}
          >
            Cancelar
          </CustomButton>
          <CustomButton
            color="secondary"
            shape="little-rounded"
            onClick={handleSaveRole}
            disabled={!canSubmitRole}
            isLoading={isCreatingRole || isUpdatingRole || isSavingPermissions}
          >
            Guardar rol
          </CustomButton>
        </DialogActions>
      </Dialog>
    </section>
  );
};

export default RolesManagement;
